import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {number} */
  #mediaLoadGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
    document.addEventListener('visibilitychange', this.#pauseWhenDocumentHidden, { signal });

    // Product videos must start directly on hover. This prevents their poster
    // colour and play button from appearing as a temporary overlay.
    if (this.closest('.product-media-container--video, .product-media-container--external_video')) {
      this.addEventListener('pointerenter', this.#playOnPointerEnter, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    const loadGeneration = ++this.#mediaLoadGeneration;
    this.setAttribute('data-media-loaded', 'true');
    this.#watchForReadyState(content, loadGeneration);
    this.appendChild(content);

    // Product-card previews use manual replay so Shopify can serve its HLS
    // source. Passing loop=true to video_tag disables that optimized source.
    if (content instanceof HTMLVideoElement && this.closest('product-card')) {
      content.addEventListener('ended', () => {
        const productCard = this.closest('product-card');
        if (!this.isConnected || !productCard?.matches(':hover')) return;

        content.currentTime = 0;
        void content.play().catch(() => {});
      });
    }

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      void content.play().catch(() => {});
    }
  }

  /**
   * Keep the preview visible until a video has a real frame ready to show.
   *
   * @param {Element} content
   * @param {number} loadGeneration
   */
  #watchForReadyState(content, loadGeneration) {
    const markReady = () => {
      if (
        loadGeneration !== this.#mediaLoadGeneration ||
        !content.isConnected ||
        this.hasAttribute('data-media-ready')
      ) {
        return;
      }

      this.setAttribute('data-media-ready', 'true');
      this.dispatchEvent(new CustomEvent('mediaready'));
    };

    const markReadyAfterPaint = () => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(markReady));
    };

    if (content instanceof HTMLVideoElement) {
      const markReadyAfterVideoFrame = () => {
        if (typeof content.requestVideoFrameCallback === 'function') {
          content.requestVideoFrameCallback(() => markReady());
        } else {
          markReadyAfterPaint();
        }
      };

      if (content.readyState >= 2) {
        markReadyAfterVideoFrame();
      } else {
        content.addEventListener('loadeddata', markReadyAfterVideoFrame, { once: true });
      }
    } else if (content instanceof HTMLIFrameElement) {
      content.addEventListener('load', markReadyAfterPaint, { once: true });
    }
  }

  /**
   * Releases a deferred video or iframe after a short-lived card preview.
   * The template and poster stay in the DOM, so a later hover can load it
   * again without retaining a decoder or an external player in the meantime.
   */
  unloadContent() {
    this.pauseMedia();
    this.#mediaLoadGeneration += 1;

    for (const content of this.querySelectorAll(':scope > video, :scope > iframe')) {
      content.remove();
    }

    this.removeAttribute('data-media-loaded');
    this.removeAttribute('data-media-ready');
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const playback = this.querySelector('video')?.play();
      if (playback) {
        void playback.catch(() => {
          this.isPlaying = false;
          this.updatePlayPauseHint(this.isPlaying);
        });
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Reveal and start a product video without moving keyboard focus.
   *
   * @param {PointerEvent} event
   */
  #playOnPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.loadContent(false);
    this.playMedia();
  };

  #pauseWhenDocumentHidden = () => {
    if (document.hidden) this.pauseMedia();
  };
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
